import React, { useEffect, useState } from 'react';
import { Button, Modal, Badge, Form } from 'react-bootstrap';
import { setIsLoading } from '../general/actions';
import { connect } from 'react-redux';
import axiosInstance from '../general/axiosInstance';
import { C } from '../general/colors';

const ACTION_STYLE = [
    { match: ['crea', 'nuevo', 'cread'],   color: C.success, bg: '#ecfdf5', icon: 'fa-plus-circle' },
    { match: ['apro'],                      color: C.primary, bg: '#eff6ff', icon: 'fa-check-circle' },
    { match: ['anul', 'cancel', 'elimin'],  color: C.danger,  bg: '#fef2f2', icon: 'fa-ban'          },
    { match: ['modif', 'actual', 'cambi'],  color: C.warning, bg: '#fffbeb', icon: 'fa-pencil'       },
];

const FIELD_LABELS = {
    delivery_date:    'Fecha entrega',
    delivery_time:    'Hora entrega',
    total_amount:     'Total',
    order_status:     'Estado',
    notes:            'Notas',
    order_detail:     'Productos',
    agreement:        'Acuerdo',
    created_at:       'Creado',
    updated_at:       'Actualizado',
};

function getActionStyle(desc = '') {
    const d = desc.toLowerCase();
    const match = ACTION_STYLE.find(s => s.match.some(k => d.includes(k)));
    return match || { color: '#94a3b8', bg: '#f8fafc', icon: 'fa-circle-o' };
}

function getInitials(name) {
    if (!name) return 'U';
    return name.split(' ').slice(0, 2).map(p => p[0] || '').join('').toUpperCase();
}

const MONTHS = ['ene.', 'feb.', 'mar.', 'abr.', 'may.', 'jun.', 'jul.', 'ago.', 'sep.', 'oct.', 'nov.', 'dic.'];

function formatDate(str) {
    if (!str) return '';
    try {
        const [datePart, timePart] = str.split(' ');
        const parts = datePart.includes('-') ? datePart.split('-') : datePart.split('/');
        const [a, b, c] = parts;
        const [day, month, year] = datePart.includes('/') ? [a, b, c] : [c, b, a];
        const monthName = MONTHS[parseInt(month, 10) - 1] ?? month;
        const base = `${day} ${monthName} ${year}`;
        if (!timePart) return base;
        const [hour, minute] = timePart.split(':');
        return `${base} · ${hour}:${minute}`;
    } catch { return str; }
}

function formatValue(val) {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'boolean') return val ? 'Sí' : 'No';
    if (Array.isArray(val)) return `${val.length} item${val.length !== 1 ? 's' : ''}`;
    if (typeof val === 'object') {
        // Show a readable label for common sub-objects
        return val.description ?? val.name ?? val.id ?? JSON.stringify(val);
    }
    return String(val);
}

function computeDiff(before, after) {
    if (!before && !after) return [];
    const b = before || {};
    const a = after  || {};
    const allKeys = new Set([...Object.keys(b), ...Object.keys(a)]);
    const changes = [];
    for (const key of allKeys) {
        if (JSON.stringify(b[key]) !== JSON.stringify(a[key])) {
            changes.push({ key, before: b[key], after: a[key] });
        }
    }
    return changes;
}

function DiffViewer({ before, after }) {
    const changes = computeDiff(before, after);
    if (!changes.length) return null;

    return (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {changes.map(({ key, before: b, after: a }) => {
                const isAdded   = b === undefined;
                const isRemoved = a === undefined;
                return (
                    <div key={key} style={{
                        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
                        background: '#fff', borderRadius: 6, padding: '4px 10px',
                        border: '1px solid #e2e8f0', fontSize: 12,
                    }}>
                        <span style={{ fontWeight: 700, color: '#7c3aed', minWidth: 90, flexShrink: 0 }}>
                            {FIELD_LABELS[key] || key}
                        </span>

                        {!isAdded && (
                            <span style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 4, padding: '1px 7px', textDecoration: isRemoved ? 'line-through' : 'none', fontFamily: 'monospace', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {formatValue(b)}
                            </span>
                        )}

                        {!isAdded && !isRemoved && (
                            <i className="fa fa-long-arrow-right" style={{ color: '#94a3b8', flexShrink: 0 }} />
                        )}

                        {!isRemoved && (
                            <span style={{ background: '#dcfce7', color: '#15803d', borderRadius: 4, padding: '1px 7px', fontFamily: 'monospace', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {formatValue(a)}
                            </span>
                        )}

                        {isAdded   && <span style={{ fontSize: 10, color: '#15803d', fontWeight: 600 }}>agregado</span>}
                        {isRemoved && <span style={{ fontSize: 10, color: '#b91c1c', fontWeight: 600 }}>eliminado</span>}
                    </div>
                );
            })}
        </div>
    );
}

function JsonViewer({ data, depth = 0 }) {
    if (data === null)      return <span style={{ color: '#94a3b8' }}>null</span>;
    if (data === undefined) return <span style={{ color: '#94a3b8' }}>—</span>;
    if (typeof data === 'boolean') return <span style={{ color: '#f59e0b', fontWeight: 600 }}>{data ? 'true' : 'false'}</span>;
    if (typeof data === 'number')  return <span style={{ color: '#2563eb', fontWeight: 600 }}>{data}</span>;
    if (typeof data === 'string')  return <span style={{ color: '#16a34a' }}>"{data}"</span>;

    if (Array.isArray(data)) {
        if (!data.length) return <span style={{ color: '#94a3b8' }}>[]</span>;
        return (
            <div style={{ paddingLeft: 14, borderLeft: '2px solid #e2e8f0', marginLeft: 2 }}>
                {data.map((item, i) => (
                    <div key={i} style={{ marginTop: 2 }}>
                        <span style={{ color: '#94a3b8', fontSize: 10, marginRight: 4 }}>[{i}]</span>
                        <JsonViewer data={item} depth={depth + 1} />
                    </div>
                ))}
            </div>
        );
    }

    if (typeof data === 'object') {
        const entries = Object.entries(data);
        if (!entries.length) return <span style={{ color: '#94a3b8' }}>{'{}'}</span>;
        return (
            <div style={{ paddingLeft: depth > 0 ? 14 : 0, borderLeft: depth > 0 ? '2px solid #e2e8f0' : 'none', marginLeft: depth > 0 ? 2 : 0 }}>
                {entries.map(([k, v]) => (
                    <div key={k} style={{ marginTop: 4, display: 'flex', alignItems: 'flex-start', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ color: '#7c3aed', fontWeight: 700, flexShrink: 0 }}>{k}:</span>
                        <span><JsonViewer data={v} depth={depth + 1} /></span>
                    </div>
                ))}
            </div>
        );
    }

    return <span>{String(data)}</span>;
}

function parseLog(raw) {
    if (!raw) return null;
    try { return typeof raw === 'object' ? raw : JSON.parse(raw); }
    catch { return null; }
}

function extractDiffParts(parsedLog, prevParsedLog) {
    if (!parsedLog) return { before: null, after: null };

    // Explicit before/after structure
    if ('before' in parsedLog || 'after' in parsedLog) {
        return { before: parsedLog.before ?? null, after: parsedLog.after ?? null };
    }

    // Full-state snapshot: compare with previous entry
    return { before: prevParsedLog ?? null, after: parsedLog };
}

function Log(props) {
    const { setIsLoading } = props;

    const [show, setShow]         = useState(props.show);
    const [orderId, setOrderId]   = useState(props.orderId);
    const [orderLog, setOrderLog] = useState([]);
    const [showDetail, setShowDetail] = useState(false);

    const onClose = () => {
        setShow(false);
        props.setShow(false);
        if (typeof props.onClose === 'function') props.onClose();
    };

    useEffect(() => { setShow(props.show); }, [props.show]);
    useEffect(() => { setOrderId(props.orderId); }, [props.orderId]);
    useEffect(() => { if (orderId) getOrderData(orderId); }, [orderId]);

    const getOrderData = async (id) => {
        setIsLoading(true);
        try {
            const r = await axiosInstance.get('/orders-log/' + id);
            setOrderLog(r.data || []);
        } catch (e) { console.log(e); }
        finally { setIsLoading(false); }
    };

    return (
        <Modal show={show} onHide={onClose} size="lg" centered>
            {/* ── Header ── */}
            <Modal.Header closeButton style={{ background: '#f8fafc', borderBottom: '1px solid var(--abi-border)', padding: '12px 20px' }}>
                <Modal.Title style={{ fontWeight: 800, fontSize: 15, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <i className="fa fa-history" style={{ color: 'var(--abi-primary)' }} />
                    Historial de cambios
                    <Badge bg="secondary" style={{ fontSize: 13, fontWeight: 600 }}>
                        Pedido #{orderId}
                    </Badge>
                </Modal.Title>
                {orderLog.length > 0 && (
                    <div style={{ marginLeft: 'auto', marginRight: 16 }}>
                        <Form.Check
                            type="switch" id="log-show-detail"
                            label={<span style={{ fontSize: 12, color: 'var(--abi-muted)' }}>Ver detalle</span>}
                            checked={showDetail}
                            onChange={e => setShowDetail(e.target.checked)}
                        />
                    </div>
                )}
            </Modal.Header>

            {/* ── Body ── */}
            <Modal.Body style={{ padding: '24px 28px', maxHeight: '65vh', overflowY: 'auto' }}>

                {orderLog.length === 0 && (
                    <div style={{ textAlign: 'center', color: 'var(--abi-muted)', padding: '48px 0' }}>
                        <i className="fa fa-inbox" style={{ fontSize: 36, opacity: 0.2, display: 'block', marginBottom: 14 }} />
                        <div style={{ fontSize: 14, fontWeight: 600 }}>Sin registros</div>
                        <div style={{ fontSize: 13, marginTop: 4 }}>No hay cambios registrados para este pedido.</div>
                    </div>
                )}

                {orderLog.length > 0 && (
                    <div style={{ position: 'relative', paddingLeft: 36 }}>

                        {/* Línea vertical */}
                        <div style={{
                            position: 'absolute', left: 11, top: 10, bottom: 10,
                            width: 2, background: 'var(--abi-border)', borderRadius: 999,
                        }} />

                        {(() => {
                            // Pre-parse all logs and resolve each entry's scope once
                            const enriched = orderLog.map(e => {
                                const parsed = parseLog(e.log);
                                // detail_id can live on the entry OR inside the log JSON
                                const scope = e.detail_id ?? parsed?.detail_id ?? null;
                                return { entry: e, parsed, scope };
                            });
                            return enriched;
                        })().map(({ entry: det, parsed: parsedLog, scope: detScope }, i, enriched) => {
                            const { color, bg, icon } = getActionStyle(det.description);
                            const isLast = i === enriched.length - 1;

                            const prevSameScope = enriched.slice(0, i).reverse().find(
                                ({ scope: s }) => detScope ? s === detScope : !s
                            );
                            const prevParsedLog = prevSameScope?.parsed ?? null;
                            const { before: diffBefore, after: diffAfter } = extractDiffParts(parsedLog, prevParsedLog);
                            const diffChanges = computeDiff(diffBefore, diffAfter);

                            return (
                                <div key={det.id} style={{ position: 'relative', marginBottom: isLast ? 0 : 18 }}>

                                    {/* Dot */}
                                    <div style={{
                                        position: 'absolute', left: -30, top: 10,
                                        width: 22, height: 22, borderRadius: '50%',
                                        background: color,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        boxShadow: `0 0 0 4px ${color}28`,
                                        zIndex: 1,
                                    }}>
                                        <i className={`fa ${icon}`} style={{ fontSize: 9, color: '#fff' }} />
                                    </div>

                                    {/* Card */}
                                    <div style={{
                                        background: bg,
                                        border: `1px solid ${color}33`,
                                        borderRadius: 12,
                                        padding: '12px 16px',
                                    }}>
                                        {/* Acción + usuario */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                                            <div>
                                                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--abi-text)' }}>
                                                    {det.description}
                                                </div>
                                                <div style={{ fontSize: 12, color: 'var(--abi-muted)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 5 }}>
                                                    <i className="fa fa-clock-o" />
                                                    {formatDate(det.created_at)}
                                                </div>
                                            </div>

                                            {det.user && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                                    <div style={{
                                                        width: 30, height: 30, borderRadius: '50%',
                                                        background: 'var(--abi-primary)',
                                                        color: '#fff', fontSize: 11, fontWeight: 700,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        flexShrink: 0,
                                                    }}>
                                                        {getInitials(det.user.name)}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--abi-text)', lineHeight: 1.2 }}>
                                                            {det.user.name}
                                                        </div>
                                                        <div style={{ fontSize: 11, color: 'var(--abi-muted)' }}>
                                                            {det.user.email}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Diff de cambios + detalle técnico */}
                                        {showDetail && diffChanges.length > 0 && (
                                            <DiffViewer before={diffBefore} after={diffAfter} />
                                        )}

                                        {showDetail && parsedLog && (
                                            <div style={{
                                                marginTop: 10,
                                                padding: '10px 14px',
                                                background: '#fff',
                                                border: '1px solid var(--abi-border)',
                                                borderRadius: 8,
                                                fontSize: 12,
                                                fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                                                maxHeight: 200,
                                                overflowY: 'auto',
                                            }}>
                                                <JsonViewer data={parsedLog} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Modal.Body>

            {/* ── Footer ── */}
            <Modal.Footer style={{ background: '#f8fafc', borderTop: '1px solid var(--abi-border)', padding: '10px 20px' }}>
                <span style={{ fontSize: 12, color: 'var(--abi-muted)', marginRight: 'auto' }}>
                    {orderLog.length > 0 && `${orderLog.length} evento${orderLog.length !== 1 ? 's' : ''} registrado${orderLog.length !== 1 ? 's' : ''}`}
                </span>
                <Button variant="outline-secondary" size="sm" onClick={onClose}>
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function mapStateToProps(state) {
    return { isLoading: state.isLoading, is_provider: state.is_provider };
}

const mapDispatchToProps = { setIsLoading };
export default connect(mapStateToProps, mapDispatchToProps)(Log);
